import { captureException } from "@sentry/react";
import { ERROR_MSG } from "@utils/enum";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getIntelHubApiUrl } from "../api";
import type { BookmarkType, RecommendationRow, Signal } from "../types";

type Params = {
  lineOfBusiness: string;
  isImpExp: boolean;
  bookmarkType: BookmarkType | null;
  refreshKey: number;
};

const toNumber = (value: unknown) => Number(value) || 0;

const formatHsCodes = (hsCodes: unknown): string => {
  if (!Array.isArray(hsCodes) || hsCodes.length === 0) return "";
  const codes = hsCodes.map((code) => (typeof code === "object" && code !== null ? (code.hs_code ?? code.code) : code)).filter(Boolean);
  if (codes.length === 0) return "";
  const visible = codes.slice(0, 2).join(", ");
  return codes.length > 2 ? `HS ${visible} +${codes.length - 2}` : `HS ${visible}`;
};

const toSignal = (value: unknown): Signal | undefined => {
  const signal = String(value || "").toLowerCase();
  return signal === "emerging" || signal === "lookalike" ? signal : undefined;
};

// Port criteria arrive as the lowercase port key ("nhava sheva, maharashtra, india"); show them title-cased.
const toTitleCase = (part: string) => (part === part.toLowerCase() ? part.replace(/(^|[\s,(/-])(\p{L})/gu, (_, sep, letter) => sep + letter.toUpperCase()) : part);

const formatCriteria = (criteria: string) =>
  criteria
    .split("|")
    .map((part) => toTitleCase(part.trim()))
    .join(" | ");

// Consolidated responses carry the matched criteria per company; older responses fall back to what the row exposes.
const getCriteria = (raw: Record<string, any>) => {
  const criteria = raw.recommendation_criteria ?? raw.criteria;
  if (Array.isArray(criteria)) return formatCriteria(criteria.filter(Boolean).join(" | "));
  return typeof criteria === "string" ? formatCriteria(criteria) : "";
};

const normalizeImpExpRow = (raw: Record<string, any>, bookmarkType: BookmarkType): RecommendationRow => {
  const total = toNumber(raw.total_doc_count);
  return {
    id: String(raw.comp_id),
    companyName: raw.company_name || "",
    countryCode: raw.country_code,
    criteria: getCriteria(raw) || formatHsCodes(raw.profile?.hs_codes) || "-",
    signal: toSignal(raw.recommendation_type),
    imports: bookmarkType === "buyer" ? total : undefined,
    exports: bookmarkType === "supplier" ? total : undefined,
    totalShipments: total,
    raw,
  };
};

const normalizeBDRow = (raw: Record<string, any>): RecommendationRow => ({
  id: String(raw.comp_id),
  companyName: raw.company_name || "",
  countryCode: raw.country_code,
  criteria: getCriteria(raw) || "-",
  signal: toSignal(raw.recommendation_type),
  imports: raw.consignee?.doc_count > 0 ? toNumber(raw.consignee.doc_count) : undefined,
  exports: raw.shipper?.doc_count > 0 ? toNumber(raw.shipper.doc_count) : undefined,
  totalShipments: toNumber(raw.total_doc_count),
  raw,
});

const useRecommendations = ({ lineOfBusiness, isImpExp, bookmarkType, refreshKey }: Params) => {
  const [rows, setRows] = useState<RecommendationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const requestIdRef = useRef(0);

  const fetchRows = useCallback(async () => {
    if (isImpExp && !bookmarkType) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const payload = isImpExp ? { value: "", type: "hscode", tab: bookmarkType, suggestions: true, persona: lineOfBusiness } : { recommendations: true, persona: lineOfBusiness };
      const { data } = await axios.post(getIntelHubApiUrl("api/intelHubDataApi"), payload);
      if (requestId !== requestIdRef.current) return;
      const isSuccess = data?.status === "success";
      const list: Record<string, any>[] = isSuccess ? (isImpExp ? data.data : data.companies) || [] : [];
      setRows(list.filter((item) => item?.comp_id).map((item) => (isImpExp ? normalizeImpExpRow(item, bookmarkType as BookmarkType) : normalizeBDRow(item))));
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      captureException(error);
      toast.error(ERROR_MSG);
      setRows([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [bookmarkType, isImpExp, lineOfBusiness]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows, refreshKey]);

  // Callers may hold `refetch` across a tab switch (e.g. a dismiss still in flight); always refetch the current tab.
  const fetchRowsRef = useRef(fetchRows);
  fetchRowsRef.current = fetchRows;
  const refetch = useCallback(() => fetchRowsRef.current(), []);

  return { rows, loading, refetch };
};

export default useRecommendations;
